import { useVirtualList } from '@root/hooks'
import { AnimatePresence, motion } from 'framer-motion'
import React from 'react'
import styled from 'styled-components'

interface Props<T> {
  items: T[]
  itemHeight?: number
  onScrollBottom?: () => void
  renderItem: (item: T, index?: number) => React.ReactNode
}

export function TableList<T extends { id: number | string }>({
  items,
  itemHeight = 72,
  onScrollBottom,
  renderItem
}: Props<T>) {
  const { scrollRef, renderItems } = useVirtualList({
    items,
    itemSize: itemHeight,
    onScrollBottom
  })

  const itemsLength = items.length

  return (
    <StyledScrollableList ref={scrollRef}>
      <AnimatePresence>
        {renderItems((item, style, index) => (
          <motion.div key={item.id} layout="position" style={style}>
            <StyledItemContainer
              key={item.id}
              $isFirst={index === 0}
              $isLast={index === itemsLength - 1}
              $itemHeight={itemHeight}
            >
              {renderItem(item, index)}
            </StyledItemContainer>
          </motion.div>
        ))}
      </AnimatePresence>
    </StyledScrollableList>
  )
}

const StyledScrollableList = styled.div`
  overflow: auto;
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;
  position: relative;
`
const StyledItemContainer = styled.div<{
  $isFirst?: boolean
  $isLast?: boolean
  $itemHeight: number
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  background-color: ${({ theme }) => theme.colors.neutralWhite};
  height: ${({ $itemHeight }) => $itemHeight}px;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ $isFirst, $isLast }) => {
    if ($isFirst && $isLast) return '1rem'
    if ($isFirst) return '1rem 1rem 0 0'
    if ($isLast) return '0 0 1rem 1rem'
    return '0'
  }};
  border-top: ${({ $isFirst }) => !$isFirst && 'none'};
  overflow: auto;

  &:hover,
  &:focus-within {
    background-color: ${({ theme }) => theme.colors.neutralGray}80;
  }
`
